import React, { useEffect, useRef, useState } from 'react';
import { Box, Container, Typography } from '@mui/material';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import SentimentSatisfiedOutlinedIcon from '@mui/icons-material/SentimentSatisfiedOutlined';
import HandDrawnArrow from '../assets/hand-drawn-spiral-arrow-3.svg';

const SquigglyArrow = ({ index, scrollProgress, rotation = 0, flipMobile = false }) => {
  const thresholds = [0.15, 0.35, 0.55];
  const threshold = thresholds[index] || 0;
  const drawDuration = 0.2;

  let progress = 0;
  if (scrollProgress > threshold) {
    progress = Math.min(1, (scrollProgress - threshold) / drawDuration);
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: { xs: '85px', sm: '50px', md: '70px', lg: '100px' },
        height: { xs: '85px', sm: '50px', md: 'auto' },
        flexShrink: 0,
        transform: {
          xs: flipMobile ? 'scaleX(-1) rotate(90deg)' : 'rotate(90deg)',
          sm: `rotate(${rotation}deg)`,
        },
      }}
    >
      <Box
        component="img"
        src={HandDrawnArrow}
        alt="arrow"
        sx={{
          width: '100%',
          height: 'auto',
          display: 'block',
          opacity: progress,
          clipPath: `inset(0 ${100 - progress * 100}% 0 0)`,
          transform: `translateX(${(1 - progress) * -8}px)`,
          transition: 'none',
        }}
      />
    </Box>
  );
};

const BenefitCircle = ({ icon, title, description, offset = 0, xsAlign = 'center' }) => (
  <Box
    sx={{
      width: { xs: 160, sm: 140, md: 180, lg: 240 },
      height: { xs: 160, sm: 140, md: 180, lg: 240 },
      borderRadius: '50%',
      backgroundColor: '#FFFFFF',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      p: 2,
      flexShrink: 0,
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
      mt: { sm: offset },
      alignSelf: { xs: xsAlign, sm: 'center' },
      mx: { xs: 3, sm: 0 },
    }}
  >
    <Box
      sx={{
        width: { xs: 32, sm: 40, md: 48 },
        height: { xs: 32, sm: 40, md: 48 },
        borderRadius: { xs: '8px', sm: '10px', md: '12px' },
        backgroundColor: '#DDE8DF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: { xs: 1, sm: 1.5, md: 2 },
      }}
    >
      {icon}
    </Box>
    <Typography
      sx={{
        fontFamily: "'Poppins', sans-serif",
        fontWeight: 700,
        fontSize: { xs: '14px', sm: '12px', md: '14px' },
        lineHeight: '18px',
        color: '#2C5E43',
        mb: 0.5,
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontFamily: "'Poppins', sans-serif",
        fontWeight: 400,
        fontSize: { xs: '11px', sm: '9px', md: '11px' },
        lineHeight: '15px',
        color: '#5A6E63',
        maxWidth: '140px',
      }}
    >
      {description}
    </Typography>
  </Box>
);

export default function BenefitsSection() {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const sectionHeight = rect.height;
      
      const start = windowHeight;
      const end = -sectionHeight;
      const current = rect.top;
      const progress = Math.max(0, Math.min(1, (start - current) / (start - end)));
      
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Box
      id="benefits"
      ref={sectionRef}
      sx={{
        width: '100%',
        py: { xs: 6, md: 10 },
        backgroundColor: '#DDE8DF',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '1440px',
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              fontSize: { xs: '22px', md: '36px' },
              color: '#325D44',
              lineHeight: { xs: '26px', md: '40px' },
              
            }}
          >
            The Benefits
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              fontSize: { xs: '18px', md: '36px' },
              color: '#325D44',
            }}
          >
            Small practice. Lasting change.
          </Typography>
        </Box>

        {/* Circles Row — zigzag layout */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 0, sm: 0 },
          }}
        >
          <BenefitCircle
            icon={<FavoriteBorderOutlinedIcon sx={{ color: '#325D44', fontSize: 24, fontWeight: 700 }} />}
            title="Reduce Stress"
            description="Lower cortisol, soften tension."
            offset={10}
            xsAlign="flex-start"
          />
          <SquigglyArrow scrollProgress={scrollProgress} index={0} rotation={-22} />
          <BenefitCircle
            icon={<PsychologyOutlinedIcon sx={{ color: '#325D44', fontSize: 24, fontWeight: 700 }} />}
            title="Improves Focus"
            description="Train calm, sustained attention"
            offset={-10}
            xsAlign="flex-end"
          />
          <SquigglyArrow scrollProgress={scrollProgress} index={1} rotation={22} flipMobile />
          <BenefitCircle
            icon={<HotelOutlinedIcon sx={{ color: '#325D44', fontSize: 24, fontWeight: 700 }} />}
            title="Better Sleep"
            description="Lower cortisol, soften tension."
            offset={10}
            xsAlign="flex-start"
          />
          <SquigglyArrow scrollProgress={scrollProgress} index={2} rotation={-22} />
          <BenefitCircle
            icon={<SentimentSatisfiedOutlinedIcon sx={{ color: '#325D44', fontSize: 24, fontWeight: 700 }} />}
            title="Emotional Balance"
            description="Lower cortisol, soften tension."
            offset={-10}
            xsAlign="flex-end"
          />
        </Box>
      </Box>
    </Box>
  );
}
